import moment from 'moment'
import {DEFAULT_LANG, SUPPORTED_LANGS, USER_LANG_KEY} from '@/constants/lang'
import {storage} from './storage'

/**
 * Get the user's preferred language from localStorage or use the default language
 * @returns The language code to use for the application
 */
export const getUserLanguage = () => {
    if (typeof window !== 'undefined') {
        const savedLanguage = storage.get(USER_LANG_KEY)
        return savedLanguage && SUPPORTED_LANGS.includes(savedLanguage)
            ? savedLanguage
            : DEFAULT_LANG
    }
    return DEFAULT_LANG
}

/**
 * Set the user's preferred language in localStorage
 * @param language - The language code to set
 * @returns void
 */
export const setUserLanguage = (language: string): void => {
    if (SUPPORTED_LANGS.includes(language)) {
        storage.set(USER_LANG_KEY, language)
    }
}

export const isGreekLanguageSelected = () => storage.get(USER_LANG_KEY) === 'el'

const ACCEPT_LANGUAGE_MAP: Record<string, string> = {
    en: 'en-GB',
    de: 'de-AT',
    es: 'es-ES',
    el: 'el-GR',
    ro: 'ro-RO',
    sl: 'sl-SI',
    sk: 'sk-SK'
}

export const getAcceptLanguage = (): string => {
    const lang = getUserLanguage()
    return ACCEPT_LANGUAGE_MAP[lang] || ACCEPT_LANGUAGE_MAP[DEFAULT_LANG] || 'en-GB'
}

/**
 * Maps language keys to moment.js locale identifiers and sets the locale
 * @param langKey - The language key (en, es, el, ro, sk, sl, de)
 * @returns Promise that resolves when locale is set
 */
export const setupMomentLocale = async (langKey: string): Promise<void> => {
    // Check if the language is supported, otherwise use default
    if (!SUPPORTED_LANGS.includes(langKey)) {
        return setupMomentLocale(DEFAULT_LANG)
    }

    const localeMap = {
        en: 'en-gb', // English (Britain)
        es: 'es', // Spanish (Spain)
        el: 'el', // Greek
        ro: 'ro', // Romanian
        sk: 'sk', // Slovak
        sl: 'sk', // Slovenian (Slovak)
        de: 'de-at' // German (Austria)
    } as const

    const locale = localeMap[langKey] || localeMap[DEFAULT_LANG] || 'en-gb'

    const config = {
        week: {
            dow: 1
        }
    } as const

    try {
        await import(`moment/locale/${locale}`)
        moment.locale(locale)
        moment.updateLocale(locale, config)
    } catch (error) {
        console.error(`Failed to load locale ${locale}:`, error)
        moment.locale('en-gb')
        moment.updateLocale('en-gb', config)
    }
}

/**
 * Dynamically imports Ant Design locale configuration based on language code
 * @param lang - The language code
 * @returns Promise that resolves to the locale configuration
 */
export const getAntdLocale = async (lang: string) => {
    switch (lang) {
        case 'de': {
            const deLocale = (await import('antd/es/locale/de_DE')).default
            return {
                ...deLocale,
                Calendar: {
                    ...deLocale.Calendar,
                    lang: {
                        ...deLocale.Calendar?.lang,
                        shortMonths: ['Jän', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
                    }
                }
            }
        }
        case 'el':
            return (await import('antd/es/locale/el_GR')).default
        case 'ro':
            return (await import('antd/es/locale/ro_RO')).default
        case 'sk':
        case 'sl':
            return (await import('antd/es/locale/sk_SK')).default
        case 'es':
            return (await import('antd/es/locale/es_ES')).default
        case 'en':
        default:
            return (await import('antd/es/locale/en_GB')).default
    }
}
